import { get, postJson, postFormData, patch, deleteRequest } from './apiClient';
import type { MissingVendorLine } from './vendorPurchaseOrderApi';

/** D45.8 — one thing a read of the client's email could not read. `detail` is written for a person. */
export interface ConfirmedOrderReadIssueDto {
  code: string;
  detail: string;
  /** The attachment it is about; '' for the email's own text. */
  filename?: string;
}

/** One line the client's email asked for, as the reader read it. */
export interface ExtractedClientItemDto {
  _id?: string;
  lineNumber?: number | null;
  itemName: string;
  itemDescription?: string;
  quantity: number | null;
  uom?: string | null;
  confidence?: number | null;
  matchedQuotationLineNumber?: number | null;
  /** D81 rule 3 — read from a PO document (the ship's whole order) or the email's text (only changes). */
  readFrom?: 'document' | 'text' | null;
}

export type ClientFollowUpKind = 'revised_po' | 'change_request' | 'cancellation' | 'other';

/**
 * D45.3 — a later email about this order. It never changed the order: a person applies it
 * (the order's lines become the list they approve) or dismisses it.
 */
export interface ClientFollowUpDto {
  _id: string;
  /** D81 rule 5 — an email, or a ship's PO a person uploaded (then there is no email). */
  source?: 'email' | 'upload';
  emailMessageId: string | null;
  /** The uploaded file (source 'upload'); `filename` is the name the person's file had. */
  file?: { filename: string; mimeType?: string; size?: number | null; sha1?: string } | null;
  /**
   * Who uploaded it: the user's name on the order's detail read (getConfirmedOrderDetail loads it),
   * the bare id on a write's answer. Shown only as a name.
   */
  uploadedBy?: string | { _id: string; firstName?: string; lastName?: string } | null;
  receivedAt?: string | null;
  from?: string;
  subject?: string;
  kind: ClientFollowUpKind;
  extractedItems: ExtractedClientItemDto[];
  /** The sentences asking for a change or a cancellation, as the client wrote them. */
  requestedChanges: string[];
  readIssues: ConfirmedOrderReadIssueDto[];
  status: 'open' | 'applied' | 'dismissed';
  recordedAt?: string;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  /** D81 — the note typed when this email was applied. */
  resolutionNote?: string;
}

export type ConfirmedOrderHoldCode =
  | 'QUOTATION_NOT_SENT'
  | 'SENDER_NOT_CLIENT'
  | 'WORDING_UNCLEAR'
  | 'QUOTATION_AMBIGUOUS';

/** D81.5 — one live version a person may pick on a QUOTATION_AMBIGUOUS hold. */
export interface ConfirmedOrderHoldCandidateDto {
  quotationId: string;
  quotationNumber?: string;
  quotationStatus?: string;
}

/** D45.7 — an email confirmation that waits for a person (EmailMessage.confirmedOrderHold). */
export interface ConfirmedOrderHoldDto {
  code: ConfirmedOrderHoldCode;
  /** null on a QUOTATION_AMBIGUOUS hold: a person picks one of `candidates`. */
  quotationId?: string | null;
  quotationNumber?: string;
  quotationStatus?: string;
  /** QUOTATION_AMBIGUOUS only. */
  candidates?: ConfirmedOrderHoldCandidateDto[];
  at?: string | null;
}

/** One vendor PO of an order, reduced to what the list words its PO state from (D104.25). */
export interface ConfirmedOrderPoFactDto {
  vendorName: string;
  status?: string; // 'draft' | 'sent' | 'amended' | 'cancelled'
}

export interface ConfirmedOrderDto {
  _id: string;
  rfqId?: string | null;
  rfqNumber?: string;
  quotationId?: string | null;
  quotationNumber?: string;
  clientName: string;
  clientEmail?: string;
  vesselName?: string;
  /**
   * The CLIENT's own purchase-order reference for this order (B6).
   *
   * Deborah, 2026-09-04: "they usually say make sure the purchase order number
   * is stated on the invoice, because when they have to verify or validate our
   * invoice, they have to tie it back with their purchase order." The client
   * invoice inherits it at generation, so it is typed here once instead of
   * being remembered at invoicing time.
   */
  clientPoNumber?: string;
  items?: any[];
  vendors?: string[];
  totalItems: number;
  totalQuote: number;
  currency?: string;
  status: string;
  confirmedAt: string;
  confirmationSource: string;
  emailFrom?: string;
  emailSubject?: string;
  notes?: string;
  linkStatus?: 'linked' | 'pending_manual';
  matchScore?: number | null;
  matchNotes?: string;
  candidateQuotationIds?: string[];
  reconciliationStatus?: 'pending' | 'done' | 'skipped';
  extractedClientItems?: ExtractedClientItemDto[];
  reconciledAt?: string | null;
  /** D45.8 — what the read of the confirming email's PO could not read. */
  extractionReadIssues?: ConfirmedOrderReadIssueDto[];
  /** D45.3 — later emails about this order. */
  clientFollowUps?: ClientFollowUpDto[];
  /** D81 — the version a quantity change is built from (sent back as `orderUpdatedAt`). */
  updatedAt?: string;
  /**
   * D104.25 — the vendor POs this order has, ANY status (cancelled included), that the list words
   * its "Supplier POs" column and Create PO button from. Sent on every row of the list read except
   * a `brief` one (the order picker draws neither). `pos: []` = read, and none exist; the
   * summary being ABSENT = not read, which is never the same thing.
   */
  poSummary?: { pos: ConfirmedOrderPoFactDto[] };
  /** D157.3 — the vessel's warehouse sticker colour on a list row (not `brief`); null = no mapping. */
  vesselStickerColor?: string | null;
}

/**
 * D81 — the ONLY thing the server accepts to change an order's quantities: per stored line (by
 * position in `items`), keep or remove and the new quantity, plus the `updatedAt` of the order
 * the change was built from. Every other field is rebuilt server-side from the stored line.
 */
export interface OrderQuantityChangeRequest {
  changes: Array<{ index: number; quantity: number | null; keep: boolean }>;
  note?: string;
  orderUpdatedAt: string;
}

export interface ConfirmedOrderListResponse {
  success: boolean;
  data: {
    confirmedOrders: ConfirmedOrderDto[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface ConfirmedOrderDetailResponse {
  success: boolean;
  data: {
    confirmedOrder: ConfirmedOrderDto;
    quotation: any;
    rfq: any;
    vendorPurchaseOrders: any[];
    /**
     * Which of this order's lines would get NO purchase order at all right now — a
     * blank or pseudo-vendor `selectedVendor`, once lines fully covered by stock/pantry
     * are netted out. Computed server-side, read-only
     * (`vendorPurchaseOrder.service.computeMissingVendorLines`), so it is visible on a
     * reload and before any PO has been generated.
     */
    missingVendorLines?: MissingVendorLine[];
    /** D157.3 — the order's vessel sticker colour, beside (not on) the order; null = no mapping. */
    vesselStickerColor: string | null;
  };
}

export interface ConfirmedOrderListFilters {
  orderRef?: string;
  clientName?: string;
  vesselName?: string;
  createdFrom?: string;
  createdTo?: string;
  minTotalQuote?: string | number;
  minTotalItems?: string | number;
}

/**
 * @param brief drop the fields a LIST never draws — chiefly `items`, which on a
 *   confirmed order is every line with its vendor options attached. The order
 *   picker (B6) re-queries on every keystroke to print four short strings, so it
 *   asks for this; the Confirmed Orders screen does not and is unaffected.
 *   Searching and paging are the server's either way — there are 50+ orders and
 *   the number only grows, so nothing here fetches them all and filters.
 * @param filters CO-05 — the list screen's per-column filter panel. Sent to the
 *   server alongside `search` and paging (confirmedOrder.service.js
 *   `listConfirmedOrders`) so a match on an unloaded page is no longer invisible.
 */
export async function fetchConfirmedOrders(
  page = 1,
  limit = 50,
  search = '',
  brief = false,
  filters: ConfirmedOrderListFilters = {}
) {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);
  if (brief) query.set('brief', '1');
  (Object.keys(filters) as (keyof ConfirmedOrderListFilters)[]).forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  });
  const res = await get<ConfirmedOrderListResponse>(`/confirmed-orders?${query.toString()}`);
  return res.data;
}

export async function fetchConfirmedOrderById(id: string) {
  const res = await get<ConfirmedOrderDetailResponse>(`/confirmed-orders/${id}`);
  return res.data;
}

/**
 * One order's label fields and its client PO number, and nothing else (B6).
 *
 * What the vendor-invoice link modal needs to NAME the order an invoice is
 * already linked to. The full read above pulls the quotation, the RFQ, every
 * purchase order and every linked vendor invoice beside it — a detail screen's
 * worth of work to print one line.
 */
export async function fetchConfirmedOrderSummary(id: string) {
  const res = await get<{ success: boolean; data: { confirmedOrder: ConfirmedOrderDto } }>(
    `/confirmed-orders/${id}?brief=1`
  );
  return res.data.confirmedOrder;
}

export async function manualConfirmOrder(payload: { quotationId?: string; rfqId?: string; notes?: string }) {
  const res = await postJson<{ success: boolean; data: ConfirmedOrderDto }>(`/confirmed-orders/manual`, payload);
  return res.data;
}

/**
 * Copy for a failed manual confirmation, chosen by the envelope's machine-readable
 * `code` (stamped in `confirmedOrder.controller.js`) — never by matching the
 * message prose, which a copy edit on the server would silently break
 * (CLAUDE.md invariant 6).
 *
 * It lives beside `manualConfirmOrder` because this is that endpoint's contract:
 * both callers of the endpoint get one spelling of the copy rather than each
 * page inventing its own.
 */
export function confirmOrderErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'QUOTATION_NOT_CONFIRMABLE': {
      // The gate refused (ClickUp 86d478wf8). The per-line detail rides in the
      // envelope's `errors`, which apiClient exposes as `validationErrors`.
      // Rendered from the SERVER's list, not from the page's own mirror: if the
      // two ever disagree, the one that actually refused is the one to show.
      const blockers = (err as { validationErrors?: unknown } | null)?.validationErrors;
      const lines = Array.isArray(blockers)
        ? blockers
            .map((b) => (b && typeof (b as { message?: unknown }).message === 'string'
              ? `• ${(b as { message: string }).message}`
              : null))
            .filter((line): line is string => line !== null)
        : [];

      const head = 'This quotation cannot be confirmed until these are fixed:';
      return lines.length > 0
        ? `${head}\n${lines.join('\n')}`
        : 'This quotation cannot be confirmed yet. Check that every line has a selling price and that no line sells below cost.';
    }
    case 'MANUAL_CONFIRM_DISABLED':
      return 'Manual confirmation is turned off in Settings. An administrator has to enable it before this quotation can be confirmed.';
    case 'QUOTATION_NOT_FOUND':
      return 'This quotation could not be found. It may have been deleted — reload the page and try again.';
    // D81 round 4: one confirmed order per RFQ.
    case 'CONFIRMED_ORDER_EXISTS_FOR_RFQ':
      return 'This RFQ already has a confirmed order, on another version of the quotation. Open that order to change it.';
    // `INTERNAL_ERROR` is what the CENTRAL error handler stamps on anything
    // unexpected. Since TECH_DEBT #219 the controller has no catch of its own,
    // so that is the code an unforeseen failure now arrives with;
    // `CONFIRM_ORDER_FAILED` is kept because a deployed server may still be
    // sending it while a release rolls out.
    case 'CONFIRM_ORDER_FAILED':
    case 'INTERNAL_ERROR':
      return 'The order could not be confirmed. Nothing has been changed. Please try again, and contact support if it keeps failing.';
    default:
      // An unrecognised (or absent) code is still not a licence to print the
      // server's prose. Say what is known and stop.
      return 'The order could not be confirmed. Please try again, and contact support if it keeps failing.';
  }
}

export async function linkConfirmedOrder(id: string, quotationId: string) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/link`,
    { quotationId }
  );
  return res.data;
}

/**
 * Copy for a refused link, keyed on the envelope's `code` (invariant 6). D81: a link rebuilds
 * the lines from the quotation, so it is refused once a person has settled the quantities by any
 * path, or stock or a vendor PO is held against the order; a change landing mid-link is refused
 * as CONFIRMED_ORDER_CHANGED.
 */
export function linkConfirmedOrderErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  switch (code) {
    case 'CONFIRMED_ORDER_LINK_LOCKED':
      return 'This order can no longer be linked to another quotation: its quantities have already been settled (finalised, a line removed or a client email applied), or stock or purchase orders are held against it. Nothing was changed.';
    case 'CONFIRMED_ORDER_CHANGED':
      return 'This order changed while it was being linked. Nothing was changed — reload the page and try again.';
    case 'QUOTATION_NOT_FOUND':
      return 'That quotation could not be found. It may have been deleted — reload the page and pick it again.';
    case 'CONFIRMED_ORDER_NOT_FOUND':
      return 'This confirmed order could not be found. It may have been deleted — reload the page.';
    default:
      return 'The order could not be linked to the quotation. Please try again, and contact support if it keeps failing.';
  }
}

export async function updateConfirmedOrder(
  id: string,
  payload: Partial<
    Pick<
      ConfirmedOrderDto,
      'notes' | 'clientName' | 'clientEmail' | 'vesselName' | 'status' | 'clientPoNumber'
    >
  >
) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(`/confirmed-orders/${id}`, payload);
  return res.data;
}

/** D81 — `orderUpdatedAt` guards the POSITION; a DELETE carries it on the query string. */
export async function deleteConfirmedOrderItem(id: string, index: number, orderUpdatedAt: string) {
  const res = await deleteRequest<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/items/${index}?orderUpdatedAt=${encodeURIComponent(orderUpdatedAt)}`
  );
  return res.data;
}

export async function reconcileConfirmedOrder(id: string, payload: OrderQuantityChangeRequest) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/reconcile`,
    payload
  );
  return res.data;
}

export async function deleteAllConfirmedOrders() {
  const res = await deleteRequest<{ success: boolean; data: { deleted: number } }>(
    `/confirmed-orders/all`
  );
  return res.data;
}

/**
 * Copy for a failed list load, keyed on `code` like the rest (invariant 6).
 *
 * TECH_DEBT #211 named the three `alert()`s on `ConfirmedOrdersPage`, but the
 * page had a fourth leak of the same kind and it is the one every user hits
 * first: `load()` put `e.message` straight into the page-level error banner, so
 * a search string Mongo could not parse rendered its regex complaint across the
 * top of the screen.
 */
/**
 * Copy for a failed vessel save, keyed on the envelope's `code` (invariant 6).
 *
 * Worth its own function rather than a generic "save failed": the vessel is how
 * an order is identified once it leaves this screen, so a person needs to know
 * whether the name they typed is now stored. The write spans four collections
 * inside one transaction, so a failure means NOTHING changed anywhere — which is
 * the reassuring half and the half a generic message would not say.
 */
export function vesselUpdateErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'CONFIRMED_ORDER_NOT_FOUND':
      return 'This confirmed order no longer exists. Reload the page.';
    case 'RFQ_NOT_FOUND':
      return 'The RFQ behind this order no longer exists, so the vessel could not be saved against it.';
    default:
      return 'The vessel name could not be saved. Nothing has been changed — try again, and contact support if it keeps failing.';
  }
}

/**
 * Copy for a failed supplier change on an order line, keyed on the envelope's `code`
 * (invariant 6); the page used to alert the server's message.
 */
export function changeSupplierErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  switch (code) {
    case 'VENDOR_NOT_IN_ITEM_OPTIONS':
      return 'That supplier has no price for this item, so it cannot be picked for this line. Nothing has been changed.';
    case 'CONFIRMED_ORDER_ITEM_NOT_FOUND':
    case 'CONFIRMED_ORDER_NOT_FOUND':
      return 'This line is no longer on the order. Reload the page.';
    case 'SUPPLIER_SOURCE_UNAVAILABLE':
      return 'Some supplier data could not be read just now, so nothing was changed. Try again in a moment.';
    case 'VAT_RATE_NOT_SET':
      return 'Set the VAT rate in Settings first.';
    // D102.12: the line moves to the new supplier's purchase order; these stop it.
    // D171.6: a client invoice stops it only once sent; a draft one follows the line.
    case 'PO_LINE_INVOICED':
      return 'This line\'s purchase order is already on a vendor invoice or a sent client invoice, so the line cannot move to another supplier. Nothing has been changed.';
    case 'PO_LINE_NOT_PRICEABLE':
      return 'This order has no TT$/US$ rate to price the line on the new supplier\'s purchase order, so it cannot move. Nothing has been changed.';
    // D105.9: a supplier with NO price takes the line at its cost; one whose price cannot be
    // stated for the line is refused.
    case 'SUPPLIER_PRICE_NOT_WORKABLE':
      return 'That supplier\'s price for this item cannot be stated for this line (no exchange rate for its currency, or a unit that does not convert), so the line cannot move to them. Nothing has been changed.';
    case 'VENDOR_NOT_IN_LIST':
      return 'Pick a supplier from the vendor list. Nothing has been changed.';
    case 'PO_LINE_UNTRACEABLE':
      return 'This line cannot be told apart from another on its purchase order, so it cannot move to the new supplier. Nothing has been changed.';
    case 'FX_RATE_NOT_SET':
      return 'Set the TT$/US$ rate in Settings first.';
    default:
      return 'The supplier could not be changed. Nothing has been changed — try again, and contact support if it keeps failing.';
  }
}

export function confirmedOrdersLoadErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'CONFIRMED_ORDERS_FETCH_FAILED':
    case 'INTERNAL_ERROR':
      return 'Confirmed orders could not be loaded. Try again, and contact support if it keeps failing.';
    default:
      return 'Confirmed orders could not be loaded. Check your connection and try again.';
  }
}

/**
 * Copy for a failed delete-all, keyed on the envelope's `code` for the same
 * reason `confirmOrderErrorMessage` is (invariant 6). TECH_DEBT #211: this call
 * used to `alert(e?.message)` on `ConfirmedOrdersPage`, which put whatever the
 * server threw — a driver error, a cast failure — in front of the user.
 *
 * Worth stating plainly in the copy that nothing was deleted, because the user
 * has just typed DELETE into a prompt and has no other way to tell whether the
 * irreversible thing happened.
 */
export function deleteConfirmedOrdersErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'CONFIRMED_ORDERS_DELETE_FAILED':
    case 'INTERNAL_ERROR':
      return 'The confirmed orders could not be deleted. Nothing has been removed. Please try again, and contact support if it keeps failing.';
    default:
      return 'The confirmed orders could not be deleted. Please reload the page to see the current state before trying again.';
  }
}


/**
 * A purchase-order line a supplier change moved (D102.12): the line leaves the old
 * supplier's PO (`from`) and joins, or starts, the new supplier's (`to`; null when
 * nothing is left to buy after stock). `change`: 'amended' (a PO the supplier has:
 * a new revision to re-send), 'updated' (a draft), 'cancelled' (a draft left empty),
 * 'created' (a new draft PO). The PATCH's `meta.poMoves`.
 */
export interface PoLineMove {
  lineNumber: number | null;
  itemName: string;
  from: { poNumber: string | null; vendorName: string; change: string };
  to: { poNumber: string | null; vendorName: string; change: string } | null;
}

/**
 * `vendorId`: the picked vendor-list entry's (D86.5), so the server stores WHICH vendor - two
 * vendors of one name are told apart. A priced supplier with no vendor-list entry sends none.
 */
export async function changeConfirmedOrderItemSupplier(
  id: string,
  itemIndex: number,
  vendorName: string,
  vendorId?: string | null
) {
  const res = await patch<{
    success: boolean;
    data: ConfirmedOrderDto;
    meta?: { poMoves?: PoLineMove[] };
  }>(`/confirmed-orders/${id}/items/${itemIndex}/supplier`, { vendorName, ...(vendorId ? { vendorId } : {}) });
  return {
    confirmedOrder: res.data,
    poMoves: res.meta?.poMoves ?? [],
  };
}

const PO_CHANGE_WORDS: Record<string, string> = {
  amended: 'amended, re-send it',
  updated: 'updated',
  cancelled: 'cancelled, it is empty',
  created: 'new draft',
};

/**
 * The one wording for "these PO lines moved to the new supplier's purchase order",
 * chosen by the change code (invariant 6), or null when no PO line moved.
 */
export function poMovesMessage(moves: PoLineMove[]): string | null {
  if (!moves.length) return null;
  const word = (change: string) => PO_CHANGE_WORDS[change] ?? change;
  const each = moves.map((m) => {
    const name = m.itemName || (m.lineNumber == null ? 'A line' : `Line ${m.lineNumber}`);
    const from = `${m.from.poNumber ?? m.from.vendorName} (${word(m.from.change)})`;
    return m.to
      ? `${name}: from ${from} to ${m.to.poNumber ?? m.to.vendorName} (${word(m.to.change)})`
      : `${name}: off ${from}, nothing left to buy`;
  });
  return `Purchase orders changed:\n${each.join('\n')}`;
}

/**
 * D45.3 — apply a client's later email: the order's quantities become what a person approved on
 * Review & Finalise (D81 request) and the email is marked applied, in one write.
 */
export async function applyConfirmedOrderFollowUp(
  id: string,
  followUpId: string,
  payload: OrderQuantityChangeRequest
) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/follow-ups/${followUpId}/apply`,
    payload
  );
  return res.data;
}

/** D45.3 — dismiss a client's later email; the order stays as it is. */
export async function dismissConfirmedOrderFollowUp(id: string, followUpId: string) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/follow-ups/${followUpId}/dismiss`,
    {}
  );
  return res.data;
}

/**
 * Copy for a refused quantity change — Finalise, Apply or Delete line (D81) — keyed on the
 * envelope's `code` (invariant 6). Per-line refusals name the line from `validationErrors`.
 */
export function orderQuantityChangeErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  const perLine = (err as { validationErrors?: unknown } | null)?.validationErrors;
  const lineNumbers = Array.isArray(perLine)
    ? perLine
        .map((e) => (e && typeof (e as { lineNumber?: unknown }).lineNumber === 'number' ? (e as { lineNumber: number }).lineNumber : null))
        .filter((n): n is number => n !== null)
    : [];
  const lines = lineNumbers.length ? ` (line${lineNumbers.length === 1 ? '' : 's'} ${lineNumbers.join(', ')})` : '';
  // A PO refusal names its purchase orders, each once.
  const poNumbers = Array.from(new Set(
    (Array.isArray(perLine) ? perLine : [])
      .map((e) => (e as { poNumber?: unknown } | null)?.poNumber)
      .filter((n): n is string => typeof n === 'string' && n !== '')
  ));
  const onPo = poNumbers.length ? poNumbers.join(', ') : 'a purchase order of this order';

  switch (code) {
    case 'CONFIRMED_ORDER_CHANGED':
      return 'This order changed since you opened it. Nothing has been changed — reload the page and make the change again.';
    case 'CONFIRMED_ORDER_VERSION_REQUIRED':
    case 'ORDER_QUANTITY_CHANGES_INVALID':
    case 'CONFIRMED_ORDER_ITEM_INDEX_INVALID':
      return 'The order has changed on screen since it loaded. Nothing has been changed — reload the page and try again.';
    case 'ORDER_LINE_QUANTITY_INVALID':
      return `Every line kept on the order needs a quantity above 0${lines}. Enter one, or untick the line to remove it.`;
    case 'ORDER_LINE_NO_UNIT_PRICE':
      return `This line has no unit price, so its quantity cannot be changed${lines}. Price the line first.`;
    case 'ORDER_LINE_CHANGES_REFUSED':
      return `Some lines cannot take the quantity given${lines}. Check their quantity and unit price.`;
    case 'ORDER_MUST_KEEP_A_LINE':
      return 'Keep at least one line on the order.';
    case 'STOCK_DRAW_NOT_CONVERTIBLE':
    case 'STOCK_RESERVATION_SHORT':
      return `The stock held for this order could not be adjusted to the new quantity${lines}. Nothing has been changed.`;
    case 'CONFIRMED_ORDER_NOT_FOUND':
      return 'This confirmed order no longer exists. Reload the page.';
    // D81: the order's purchase orders follow the change; a PO line that cannot
    // be traced to its order line refuses it rather than guess.
    case 'VENDOR_PO_LINE_SOURCE_NOT_FOUND':
      return `A line on ${onPo} can no longer be traced to its order line, so the purchase orders cannot follow this change. Nothing has been changed — contact support.`;
    // D171.5: a bill line pinned by hand names its PO line by position, so no line may leave that draft PO.
    case 'VENDOR_PO_LINE_PINNED':
      return `A vendor invoice line is paired by hand with a line of ${onPo}, so no line can be taken off it. Unpair it on that purchase order's Invoice Reconciliation panel first. Nothing has been changed.`;
    // D81: the order's client invoices follow the change, or refuse it (server: clientInvoice.service `applyOrderLineChange`).
    case 'CLIENT_INVOICE_ALREADY_SENT':
      return 'The client invoice for this order has already been sent, so it cannot follow this change. Nothing has been changed — use a credit note.';
    case 'CLIENT_INVOICE_HAS_CREDIT_NOTE':
      return 'The client invoice for this order already has a credit note against it, so it cannot follow this change. Nothing has been changed.';
    case 'CLIENT_INVOICE_LINE_DELIVERED':
      return 'A line this change touches has already been delivered on the client invoice. Nothing has been changed.';
    case 'CLIENT_INVOICE_LINE_RETURNED':
      return 'A line this change touches has a goods return on the client invoice. Nothing has been changed.';
    case 'CLIENT_INVOICE_LINE_SOURCED':
      return 'A line this change touches has pantry, stock or vendor quantities entered on the client invoice. Nothing has been changed.';
    case 'CLIENT_INVOICE_LINE_OUT_OF_STEP':
      return 'A line on the client invoice no longer matches the order line it bills. Nothing has been changed.';
    case 'CLIENT_INVOICE_LINE_QUANTITY_EDITED':
      return 'A line this change touches bills a quantity a person changed on the client invoice. Nothing has been changed.';
    case 'CLIENT_INVOICE_LINE_UNIT_EDITED':
      return 'A line this change touches is billed on the client invoice in another unit than the order line. Nothing has been changed.';
    case 'CLIENT_INVOICE_LINE_PRICE_EDITED':
      return 'A line this change touches is priced on the client invoice differently from the order: a person changed its price. Nothing has been changed.';
    case 'CLIENT_INVOICE_LINE_PRICE_UNKNOWN':
      return "A line this change touches cannot be re-priced on the client invoice: the order line's price at its old quantity cannot be worked out. Nothing has been changed.";
    default:
      return 'The order could not be updated. Nothing has been changed — try again, and contact support if it keeps failing.';
  }
}

/**
 * Copy for a failed Apply or Dismiss, keyed on the envelope's `code` (invariant 6).
 */
export function followUpErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'CONFIRMED_ORDER_FOLLOW_UP_ALREADY_RESOLVED':
      return 'Someone has already applied or dismissed this email. Reload the page to see the order as it is now.';
    case 'CONFIRMED_ORDER_FOLLOW_UP_NOT_FOUND':
      return 'This email is no longer on the order. Reload the page.';
    default:
      return orderQuantityChangeErrorMessage(err);
  }
}

/**
 * D81 rule 5 — upload a ship's PO that did not come by email (WhatsApp, a portal). It is read like
 * an emailed PO and recorded as an open follow-up on the order; returns the order.
 */
export async function uploadShipPo(id: string, file: File) {
  const form = new FormData();
  form.append('file', file);
  const res = await postFormData<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/ship-po`,
    form
  );
  return res.data;
}

/**
 * The file picker's hint: the order-document types the server reads (confirmedOrderShipPo.service
 * `isOrderDocument`, by extension). A hint only — the route refuses anything else
 * (CONFIRMED_ORDER_SHIP_PO_FILE_TYPE).
 */
export const SHIP_PO_ACCEPT = '.pdf,.xlsx,.xls,.csv,.docx,.png,.jpg,.jpeg,.webp';

/** Copy for a failed ship's-PO upload, keyed on the envelope's `code` (invariant 6). */
export function uploadShipPoErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'CONFIRMED_ORDER_SHIP_PO_ALREADY_UPLOADED':
      return 'This file has already been uploaded to this order.';
    case 'CONFIRMED_ORDER_SHIP_PO_FILE_REQUIRED':
      return "Choose the ship's PO file to upload.";
    case 'CONFIRMED_ORDER_SHIP_PO_FILE_TYPE':
      return "The ship's PO must be a PDF, Excel, CSV, Word (.docx) or image file.";
    case 'CONFIRMED_ORDER_SHIP_PO_FILE_EMPTY':
      return 'That file is empty. Choose the file again, or ask the ship to resend it.';
    case 'CONFIRMED_ORDER_NOT_OPEN':
      return "This order is no longer confirmed, so a ship's PO cannot be added to it.";
    case 'CONFIRMED_ORDER_NOT_FOUND':
      return 'This confirmed order no longer exists. Reload the page.';
    case 'FILE_STORAGE_FAILED':
      return 'The file could not be stored, so nothing was recorded. Please try again.';
    default:
      return "The ship's PO could not be uploaded. Nothing has been recorded — try again, and contact support if it keeps failing.";
  }
}

/**
 * D45.7 — confirm an email that was not confirmed automatically. Runs the manual confirmation, so
 * its refusals are that endpoint's (see `confirmHeldEmailErrorMessage`).
 *
 * D81 decision 2: `quotationId` is the person's pick — required by the server on a
 * QUOTATION_AMBIGUOUS hold (one of its `candidates`); on any other hold it may be left out.
 */
export async function confirmHeldEmailOrder(emailId: string, quotationId?: string) {
  const res = await postJson<{ success: boolean; data: ConfirmedOrderDto; message?: string }>(
    `/confirmed-orders/email-holds/${emailId}/confirm`,
    quotationId ? { quotationId } : {}
  );
  return { confirmedOrder: res.data, message: res.message };
}

export function confirmHeldEmailErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  switch (code) {
    case 'CONFIRMED_ORDER_HOLD_NOT_FOUND':
      return 'This email is no longer waiting to be confirmed. Reload the email to see where it stands.';
    case 'CONFIRMED_ORDER_HOLD_AMBIGUOUS':
      return 'This email could confirm more than one quotation. Pick the one the client confirmed, then confirm again.';
    case 'CONFIRMED_ORDER_HOLD_QUOTATION_NOT_A_CANDIDATE':
      return 'That quotation is not one this email is waiting on. Reload the email and pick again.';
    default:
      return confirmOrderErrorMessage(err);
  }
}

/**
 * D81 — "Not an order — make it an RFQ" on a held email: the server makes its RFQ (the routing
 * owner) and clears the hold. `rfqCount`: the RFQs the email has now (0 when nothing in it could be
 * one).
 */
export async function markHeldEmailNotAnOrder(emailId: string) {
  const res = await postJson<{ success: boolean; data: { rfqCount: number } }>(
    `/confirmed-orders/email-holds/${emailId}/not-an-order`,
    {}
  );
  return res.data;
}

/** Copy for a refused "Not an order", keyed on the envelope's `code` (invariant 6). */
export function heldEmailNotAnOrderErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  switch (code) {
    case 'CONFIRMED_ORDER_HOLD_NOT_FOUND':
      return 'This email is no longer waiting for a person. Reload the email to see where it stands.';
    case 'HELD_EMAIL_RFQ_NOT_CREATED':
      return 'The RFQ could not be created, so the email is still waiting here. Try again, and contact support if it keeps failing.';
    default:
      return 'The email could not be made an RFQ; it is still waiting here. Try again, and contact support if it keeps failing.';
  }
}

const QUOTATION_STATUS_LABEL: Record<string, string> = {
  needs_review: 'needs review',
  finalized: 'finalized',
  sent: 'sent',
  rejected: 'rejected',
};

/**
 * The plain reason an email confirmation waits (D45.5 / D45.6), built from the hold's `code` —
 * the server sends no prose for it. Shown after "Not confirmed automatically: ".
 */
export function confirmedOrderHoldReason(hold: ConfirmedOrderHoldDto): string {
  const quotation = hold.quotationNumber || 'the quotation';
  switch (hold.code) {
    case 'QUOTATION_NOT_SENT': {
      const status = QUOTATION_STATUS_LABEL[String(hold.quotationStatus || '')];
      return `${quotation} has not been sent to the client${status ? ` (its status was ${status})` : ''}.`;
    }
    case 'SENDER_NOT_CLIENT':
      return `the sender is not an address saved on ${quotation}'s client, and the email is not a reply in the RFQ's conversation.`;
    // D81.3
    case 'WORDING_UNCLEAR':
      return `this email looks like an order for ${quotation} but doesn't clearly confirm it — confirm it if it is.`;
    // D81.5 and D81 3i decision 3: several live versions, or a subject and reply naming quotations
    // of different RFQs — worded for both.
    case 'QUOTATION_AMBIGUOUS':
      return 'this email could confirm more than one quotation — pick the quotation the client confirmed.';
    default:
      return 'a person needs to check it.';
  }
}

/** One candidate of a QUOTATION_AMBIGUOUS hold as the picker names it: its number and status. */
export function holdCandidateLabel(candidate: ConfirmedOrderHoldCandidateDto): string {
  const status = QUOTATION_STATUS_LABEL[String(candidate.quotationStatus || '')];
  return `${candidate.quotationNumber || '(no number)'}${status ? ` (${status})` : ''}`;
}
